import { css, Theme } from '@emotion/react';
import { FileUpload } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Dialog, DialogTitle, Divider } from '@mui/material';
import { Formik } from 'formik';
import { useMemo, useState } from 'react';

import { formatISO8601Date } from '@jebel/utils';

import { CheckboxField, MentionsField, SwitchField } from 'shared/components/form';
import {
  Button,
  Typography,
  MediaInput,
  DateTimePickerField,
  DatePickerField,
  IconButtonEmojis,
} from 'shared/components/ui';
import { MentionPosition } from 'shared/features/mentions';
import { useCurrentUser, useResponsive, useToast } from 'shared/hooks';
import { required } from 'shared/utils/form';
import { recordError } from 'shared/utils/record';

import { Container } from './PostModalCreate.styles';
import { PostModalCreateFormValues } from './PostModalCreate.types';
import { PostModalCreateLinksPreview } from './PostModalCreateLinksPreview';

const MAX_FILES = 10;

const textContainerCSS = css`
  display: flex;
  flex-direction: column;
  justify-content: end;
  position: relative;
`;

const textOptionsCSS = css`
  display: flex;
  justify-content: flex-end;
  padding: 0.5rem;
  position: absolute;
  z-index: 3;
`;

const uploadFileBoxCSS = (theme: Theme) => css`
  cursor: pointer;
  background-color: #f6f8fa;
  border: 1px dashed #bbbbbb;
  border-radius: 5px;
  height: 150px;
  display: grid;
  grid-template-rows: min-content min-content;
  place-items: center;
  align-content: center;

  :hover {
    background-color: ${theme.palette.action.hover};
  }
`;

const uploadIconCSS = (theme: Theme) => css`
  font-size: 3rem;
  fill: ${theme.palette.primary.light};
`;

const uploadPictureCSS = (theme: Theme) => css`
  color: ${theme.palette.primary.light};
  max-width: 40%;
  text-align: center;
`;

interface ExtraFields {
  /**
   * Show the `legacyDate` in the form.
   * @deprecated Use `withLegacy` instead.
   * @default false
   */
  legacyDate?: boolean;

  /**
   * Show the `scheduleDate` in the form.
   * @deprecated Use `withSchedule` instead.
   * @default false
   */
  scheduleDate?: boolean;
}

export interface Props {
  isOpen: boolean;
  isEdit?: boolean;
  initialValues?: Partial<PostModalCreateFormValues>;
  /** @deprecated Use the `with...` (`withSchedule`, `withLegacyDate`, ...) fields instead. */
  extraFields?: ExtraFields;

  /**
   * Show the `legacyDate` in the form.
   * @default false
   */
  withLegacyDate?: boolean;

  /**
   * Show the `scheduleDate` in the form.
   * @default false
   */
  withSchedule?: boolean;

  /**
   * Show the `scheduleDate` in the form.
   * @default false
   */
  withPinned?: boolean;

  /**
   * Whether this content will be moderated.
   * @default true
   */
  withModeration?: boolean;

  /**
   * Callback to close the modal.
   */
  onClose(): void;

  /**
   * Callback to create the post.
   */
  onPostCreate(data: PostModalCreateFormValues, isScheduled?: boolean): Promise<void>;
}

export function PostModalCreate({
  isOpen,
  initialValues,
  isEdit = false,
  onPostCreate,
  extraFields,
  ...props
}: Props) {
  const [isLoading, setLoading] = useState(false);
  const [mentions, setMentions] = useState<MentionPosition[]>([]);

  const { isMobile } = useResponsive();
  const { isAdmin } = useCurrentUser();
  const { showError } = useToast();

  const withPinned = Boolean(props.withPinned ?? isAdmin);
  const withLegacyDate = Boolean(props.withLegacyDate ?? extraFields?.legacyDate);
  const withScheduleDate = Boolean(props.withSchedule ?? extraFields?.scheduleDate);
  const withModeration = props.withModeration ?? true;

  const initial = useMemo<PostModalCreateFormValues>(() => {
    const payload: PostModalCreateFormValues = {
      text: initialValues?.text ?? '',
      isPinned: false,
      commentsAllowed: true,
      legacyDate: null,
      postDate: formatISO8601Date(),
      media: undefined,

      ...initialValues,
    };

    if (withLegacyDate && initialValues?.legacyDate) {
      payload.legacyDate = formatISO8601Date(initialValues.legacyDate);
    }

    return payload;
  }, [initialValues, withLegacyDate]);

  const submitLabel = useMemo(() => {
    if (isLoading && isEdit) {
      return 'Updating post...';
    }

    if (isLoading && !isEdit) {
      return 'Posting...';
    }

    if (isEdit) {
      return 'Update Post';
    }

    return 'Post';
  }, [isLoading, isEdit]);

  const handleClose = () => {
    props.onClose();
  };

  const handleSubmit = async (data: PostModalCreateFormValues) => {
    setLoading(true);

    const isScheduled = Boolean(data.isScheduledPost);
    const isPinned = Boolean(data.isPinned);

    try {
      const text = (data.text && data.text.trim()) ?? '';

      if (isScheduled && !data.postDate) {
        throw new Error('Should define a post-date in schedule mode');
      }

      if (!text && data.media?.length === 0) {
        throw new Error('Either text or media must be defined');
      }

      const formattedData: PostModalCreateFormValues = {
        text,
        media: data.media,
        commentsAllowed: !!data.commentsAllowed,
        isPinned,
        mentions,
      };

      if (withLegacyDate && data.legacyDate) {
        formattedData.legacyDate = data.legacyDate;
      }

      if (withScheduleDate && !isScheduled) {
        // Prevent to send the post date when was not checked.
        formattedData.postDate = formatISO8601Date();
      }

      if (isScheduled && data.postDate) {
        formattedData.postDate = data.postDate;
      }

      await onPostCreate(formattedData);

      setLoading(false);
    } catch (err) {
      setLoading(false);
      recordError(err);

      if (err instanceof Error) {
        showError(err.message);
      }
    }
  };

  return (
    <Dialog fullScreen={false} fullWidth={true} maxWidth="sm" open={isOpen} onClose={handleClose}>
      <DialogTitle>{isEdit ? 'Edit Post' : 'Post'}</DialogTitle>

      <Divider />

      <Formik initialValues={initial} validateOnBlur onSubmit={handleSubmit}>
        {form => (
          <Container>
            {withScheduleDate && (
              <CheckboxField label="Schedule Post" color="primary" name="isScheduledPost" />
            )}

            {form.values.isScheduledPost && (
              <DateTimePickerField
                label="POST DATE"
                disablePast
                name="postDate"
                inputVariant="outlined"
                validate={required}
              />
            )}

            {withLegacyDate && (
              <DatePickerField
                name="legacyDate"
                allowKeyboardControl
                inputVariant="outlined"
                label="Legacy Date"
                disableFuture
                validate={required}
              />
            )}

            <Box css={textContainerCSS}>
              <MentionsField
                name="text"
                multiline
                spellCheck
                allowSearchSpace
                placeholder="Type @ to mention some users, organizations or groups"
                onChangeMentions={setMentions}
              />

              {!isMobile && (
                <Box css={textOptionsCSS}>
                  <IconButtonEmojis
                    onSelect={emoji => form.setFieldValue('text', form.values.text + emoji)}
                  />
                </Box>
              )}
            </Box>

            {!withModeration && (
              <Alert severity="warning">
                <AlertTitle>Review your content before posting</AlertTitle>
                This post will <strong>NOT</strong> be auto-moderated and will be published to the
                community.
              </Alert>
            )}

            <Box display="flex" alignItems="center" flexWrap="wrap" gap="1rem">
              <SwitchField name="commentsAllowed" label="Allow Comments" />

              {withPinned && <SwitchField name="isPinned" label="Pin Content" />}
            </Box>

            <PostModalCreateLinksPreview />

            <MediaInput
              descriptionPlacement="top"
              maxFiles={MAX_FILES}
              initialValue={initialValues?.media}
              pickerOptions={{
                accept: ['image/*', 'video/mp4', 'video/mpeg', 'video/webm'],
              }}
              onChange={files => form.setFieldValue('media', files)}
              showDescription={true}
            >
              <Box css={uploadFileBoxCSS}>
                <FileUpload css={uploadIconCSS} />
                <Typography css={uploadPictureCSS} variant="inherit">
                  Upload pictures or videos here
                </Typography>
              </Box>
            </MediaInput>

            <Box display="flex" gap="0.5rem" justifyContent="flex-end">
              <Button onClick={handleClose} disabled={isLoading} color="primary" variant="text">
                Cancel
              </Button>

              <Button
                color="primary"
                disableElevation
                variant="contained"
                type="button"
                disabled={isLoading || form.isSubmitting}
                loading={isLoading}
                onClick={() => form.submitForm()}
              >
                {submitLabel}
              </Button>
            </Box>
          </Container>
        )}
      </Formik>
    </Dialog>
  );
}
